import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'

// Libs
import { fetchRefreshToken } from '@/api/fetchRefreshToken'
import handleLogout from '@/methods/handleLogout'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { STORAGE_DATA } from '@/constants/storageData'
import { ERROR_CODE } from '@/constants/errorData'

export const useRefreshToken = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [needRefreshToken, setNeedRefreshToken] = useState(false)
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tokenFromLocalStorage = (typeof window !== 'undefined' && JSON.parse(<string>window.localStorage.getItem(STORAGE_DATA.TOKEN))) || {}
  const isTimeout = typeof window !== 'undefined' && JSON.parse(<string>window.localStorage.getItem(STORAGE_DATA.IS_TIMEOUT))

  const router = useRouter()
  const { t } = useTranslation()
  const { query } = router

  const { data = {}, isFetched } = <any>(
    useQuery([QUERY_KEY.FETCH_REFRESH_TOKEN], () => fetchRefreshToken({ refreshToken: tokenFromLocalStorage?.refreshToken || query?.refreshToken }), {
      enabled: Boolean(isTimeout) && router.isReady && (!!tokenFromLocalStorage?.refreshToken || !!query?.refreshToken),
      onSuccess: () => window.localStorage.removeItem(STORAGE_DATA.IS_TIMEOUT),
      onError: (error: any) => {
        if (error?.errorCode === ERROR_CODE.USER_DELETED) {
          toast.error(t('Toast_User_Deleted') as string)
          if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current)
          }
          logoutTimerRef.current = setTimeout(() => {
            handleLogout()
          }, 2000)
        } else {
          handleLogout()
        }
      },
    })
  )

  const handleDetectionRefresh = () => {
    const isTimeout = typeof window !== 'undefined' && JSON.parse(<string>window.localStorage.getItem(STORAGE_DATA.IS_TIMEOUT))

    if (isTimeout) {
      setNeedRefreshToken(true)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleDetectionRefresh)
    }

    return () => {
      window.removeEventListener('storage', handleDetectionRefresh)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isFetched && !isEmpty(data)) {
      const { authToken, refreshToken } = data

      const tokenData = { token: authToken, refreshToken }

      window.localStorage.setItem(STORAGE_DATA.TOKEN, JSON.stringify(tokenData))
      setNeedRefreshToken(false)
    }
  }, [isFetched, data])
}
